import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { getBrands } from '@fh-components/fh-js-api-core/store/slices/profileSlice';
import { BreadCrumbs, Container } from '@/containers';
import { useSelector } from 'react-redux';
import { allFilterBrands } from './constants';
import s from './Brands.module.scss';
import { Title } from '@/components';
import { getCorrectName } from '@/utils';

const Brands = ({ seoData }) => {
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const brands = useSelector(getBrands) || [];
	const topBrands = useMemo(() => brands.filter(({ is_premium }) => is_premium), [brands]);
	const [activeLink, setActiveLink] = useState(allFilterBrands[0].name);
	const [currentBrands, setCurrentBrands] = useState(topBrands);
	const [isTopBrands, setIsTopBrands] = useState(true);
	const ruBrands = useMemo(
		() => brands.filter(({ name }) => name[0].toUpperCase().match(/^[А-ЯЁ]$/)),
		[brands],
	);
	const numberBrands = useMemo(() => brands.filter(({ name }) => name[0].match(/^[0-9]$/)), [
		brands,
	]);

	useEffect(() => {
		setCurrentBrands(topBrands);
	}, [topBrands]);

	const isDisabledEmptyLetter = useCallback(
		brandName => brands.some(({ name }) => name[0].toLowerCase() === brandName.toLowerCase()),
		[brands],
	);
	const isDisabledItemBrand = useCallback(
		(brandName, key) =>
			(!key && !isDisabledEmptyLetter(brandName)) ||
			(key === 'ru' && !ruBrands.length) ||
			(key === 'number' && !numberBrands.length),
		[isDisabledEmptyLetter, ruBrands, numberBrands],
	);

	const handleClick = useCallback(
		(brandName, key) => {
			if (isDisabledItemBrand(brandName, key)) {
				return;
			}

			setActiveLink(brandName);
			setIsTopBrands(false);

			switch (key) {
				case 'all':
					setCurrentBrands(brands);
					break;

				case 'number':
					setCurrentBrands(numberBrands);
					break;

				case 'ru':
					setCurrentBrands(ruBrands);
					break;

				case 'top':
					setIsTopBrands(true);
					setCurrentBrands(topBrands);
					break;

				default:
					setCurrentBrands(
						brands.filter(({ name }) => name[0]?.toLowerCase() === brandName?.toLowerCase()),
					);
					break;
			}
		},
		[
			brands,
			setActiveLink,
			setCurrentBrands,
			setIsTopBrands,
			isDisabledItemBrand,
			numberBrands,
			ruBrands,
			topBrands,
		],
	);

	const breadcrumbs = [{ name: 'Бренды', slug: 'brands' }];

	return (
		<>
			<Container>
				<div className={s.breadCrumbs}>
					<BreadCrumbs breadcrumbs={breadcrumbs} />
				</div>
			</Container>
			<div className={s.container}>
				<Title component='h1' className={s.title}>
					{seoData?.h1 ?? 'Бренды'}
				</Title>
				<div className={s.filter}>
					{allFilterBrands.map(({ name, key }) => (
						<button
							type='button'
							key={name}
							onClick={() => handleClick(name, key)}
							className={clsx(
								s.filterItem,
								activeLink === name && s.active,
								isDisabledItemBrand(name, key) && s.disable,
							)}
						>
							{name}
						</button>
					))}
				</div>
				{isTopBrands ? (
					<div className={s.topBrands}>
						{currentBrands.map(({ name, url, image }) => {
							if (image?.x2) {
								return url ? (
									<a key={url} className={s.topBrandsItem} href={url}>
										<img className={s.img} src={image?.x2} alt={name} />
									</a>
								) : null;
							}

							return (
								<div className={s.topBrandsItem} key={name}>
									{name}
								</div>
							);
						})}
					</div>
				) : (
					<div className={s.brands}>
						{currentBrands.map(({ name, url }) =>
							url ? (
								<a key={url} className={s.brandItem} href={url}>
									{name}
								</a>
							) : null,
						)}
					</div>
				)}
			</div>
		</>
	);
};

export default Brands;
